import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { RoleProvider } from '@/context/RoleProvider';

/** Long enough for the last staggered section to finish rising in. */
const PAGE_ENTER_MS = 1000;

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const [settledPath, setSettledPath] = useState<string | null>(null);

  // Each page's sections rise in when it opens: the class is on from the new
  // page's first render and comes off afterwards, so things added later (a
  // modal, a new row) appear without a delay.
  const entering = settledPath !== pathname;
  useEffect(() => {
    const timer = window.setTimeout(() => setSettledPath(pathname), PAGE_ENTER_MS);
    return () => window.clearTimeout(timer);
  }, [pathname]);

  return (
    <RoleProvider>
      <div className="flex min-h-screen bg-background-100">
        {/* Desktop sidebar */}
        <aside data-print-hide className="hidden w-64 shrink-0 lg:block">
          <div className="fixed inset-y-0 left-0 w-64">
            <Sidebar />
          </div>
        </aside>

        {/* Mobile drawer */}
        {mobileOpen ? (
          <div data-print-hide className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-foreground-950/40 animate-fade-in"
              onClick={() => setMobileOpen(false)}
              aria-hidden="true"
            />
            <div className="absolute inset-y-0 left-0 w-72 animate-slide-in-left">
              <Sidebar onNavigate={() => setMobileOpen(false)} />
            </div>
          </div>
        ) : null}

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Pinned here (not on the header itself), so it stays on screen while the page scrolls. */}
          <div data-print-hide className="sticky top-0 z-30">
            <Topbar onMenuClick={() => setMobileOpen(true)} />
          </div>
          <main className={`flex-1 ${entering ? 'page-enter' : ''}`}>
            <Outlet />
          </main>
        </div>
      </div>
    </RoleProvider>
  );
}
